import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import insightsApi from '../../api/insightsApi';
import AiMarkdown from '../../pages/impacts/AiMarkdown';
import TypingIndicator from './TypingIndicator';
import { openPromptaChat } from './promptaChat';

// ── The By-group tab's in-card chat ──────────────────────────────────────────
// Follow-up questions about the ONE selection on screen (the whole wave, an
// area or a group), answered from that selection's data and streamed.
//
// Threads are ephemeral and per selection: a map keyed `reportId:scope_key`,
// held for the life of the tab and never persisted. A turn is
//
//   { id, question, status, answer, redirect?, error? }
//   status  pending    sent, nothing back yet
//           streaming  meta arrived; tokens append to `answer`
//           done       finished (a `replace` may have swapped `answer`)
//           redirect   no answer: `redirect.text` + the Prompta-chat button
//           error      `error` is shown inline

// Prior messages sent with each question. The BE keeps 8 as well; only real
// answers go back — a redirect or an error has nothing to follow up on.
const HISTORY_MESSAGES = 8;

export const threadKey = (reportId, scopeKey) => `${reportId}:${scopeKey}`;

export function historyOf(thread) {
    const messages = [];
    (thread || []).forEach((turn) => {
        if (turn.status === 'done' && turn.answer.trim()) {
            messages.push({ role: 'user', content: turn.question });
            messages.push({ role: 'assistant', content: turn.answer });
        }
    });
    return messages.slice(-HISTORY_MESSAGES);
}

// Every selection's thread, and `ask` to add a turn to one. A stream keeps
// writing to ITS thread when the selection changes; all streams abort when
// the tab unmounts.
export function useScopedChats(reportId) {
    const [threads, setThreads] = useState({});
    const aborts = useRef(new Set());
    const nextId = useRef(0);

    useEffect(() => () => {
        aborts.current.forEach((abort) => abort());
        aborts.current.clear();
    }, []);

    const ask = useCallback((scopeKey, question, history) => {
        const key = threadKey(reportId, scopeKey);
        nextId.current += 1;
        const id = nextId.current;
        const update = (patch) => setThreads((prev) => ({
            ...prev,
            [key]: (prev[key] || []).map((turn) => (turn.id === id ? { ...turn, ...patch(turn) } : turn)),
        }));
        setThreads((prev) => ({
            ...prev,
            [key]: [...(prev[key] || []), { id, question, status: 'pending', answer: '' }],
        }));

        let abort = null;
        const finish = () => { if (abort) aborts.current.delete(abort); };
        abort = insightsApi.streamGroupScoresChat(
            reportId,
            { scope_key: scopeKey, question, history },
            {
                onMeta: () => update((turn) => (turn.status === 'pending' ? { status: 'streaming' } : {})),
                onToken: (t) => update((turn) => ({ status: 'streaming', answer: turn.answer + t })),
                onRedirect: (data) => update(() => ({ status: 'redirect', answer: '', redirect: data || {} })),
                onReplace: (data) => update(() => ({ answer: String(data?.text || '') })),
                onError: (err) => {
                    update(() => ({ status: 'error', error: err?.error || '' }));
                    finish();
                },
                onDone: () => {
                    update((turn) => (['pending', 'streaming'].includes(turn.status) ? { status: 'done' } : {}));
                    finish();
                },
            },
        );
        if (typeof abort === 'function') aborts.current.add(abort);
    }, [reportId]);

    return { threads, ask };
}

function UserBubble({ children }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
                data-testid="group-chat-question"
                sx={{
                    maxWidth: '85%', px: 1.5, py: 1, borderRadius: '12px 12px 4px 12px',
                    bgcolor: '#1565c0', color: '#fff', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function AnswerBubble({ turn }) {
    const waiting = turn.status === 'pending' || (turn.status === 'streaming' && !turn.answer);
    return (
        <Box
            data-testid="group-chat-answer"
            data-status={turn.status}
            sx={{
                maxWidth: '92%', px: 1.75, py: 1.25, borderRadius: '12px 12px 12px 4px',
                bgcolor: '#fff', border: '1px solid rgba(21, 101, 192, 0.14)',
            }}
        >
            {waiting && <TypingIndicator />}
            {turn.status === 'redirect' && (
                <Box data-testid="group-chat-redirect">
                    <Typography variant="body2" sx={{ color: '#33445c', lineHeight: 1.55 }}>
                        {turn.redirect?.text}
                    </Typography>
                    <Button
                        data-testid="group-chat-open-prompta"
                        size="small"
                        variant="outlined"
                        startIcon={<ForumOutlinedIcon fontSize="small" />}
                        onClick={() => openPromptaChat(turn.redirect?.question || turn.question)}
                        sx={{ mt: 1.25, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                        <FormattedMessage id="groupScores.chat_open_prompta" defaultMessage="Ask in Prompta chat" />
                    </Button>
                </Box>
            )}
            {turn.status === 'error' && (
                <Typography data-testid="group-chat-error" variant="body2" sx={{ color: '#c53434' }}>
                    <FormattedMessage id="groupScores.chat_error" defaultMessage="Couldn't get an answer. Please try again." />
                </Typography>
            )}
            {['streaming', 'done'].includes(turn.status) && turn.answer && (
                <AiMarkdown sx={{ color: '#33445c' }}>{turn.answer}</AiMarkdown>
            )}
        </Box>
    );
}

// The panel inside the Focus-areas card, once its "Chat about this" button is
// pressed. `onAsk(question)` sends a turn; the
// input is locked while this selection's last turn is still in flight.
export default function GroupScoresChat({ name, thread, onAsk, sx }) {
    const intl = useIntl();
    const [draft, setDraft] = useState('');
    const turns = thread || [];
    const busy = turns.some((turn) => turn.status === 'pending' || turn.status === 'streaming');
    const send = () => {
        const question = draft.trim();
        if (!question || busy) return;
        onAsk(question);
        setDraft('');
    };
    return (
        <Box
            data-testid="group-chat"
            sx={{ mt: 1.5, p: { xs: 2, sm: 2.25 }, borderRadius: 3, bgcolor: '#f7f9fc', border: '1px solid #e6ebf2', ...sx }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChatBubbleOutlineIcon fontSize="small" sx={{ color: '#1565c0' }} />
                <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 700, color: '#0d47a1' }}>
                    <FormattedMessage id="groupScores.chat_title" defaultMessage="Ask about {name}" values={{ name }} />
                </Typography>
            </Box>
            <Typography variant="caption" component="p" sx={{ color: '#8493a6', mt: 0.25 }}>
                <FormattedMessage id="groupScores.chat_note" defaultMessage="Answers use only this selection's scores and responses. Nothing is saved." />
            </Typography>

            {turns.length > 0 && (
                <Box data-testid="group-chat-thread" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.75 }}>
                    {turns.map((turn) => (
                        <React.Fragment key={turn.id}>
                            <UserBubble>{turn.question}</UserBubble>
                            <AnswerBubble turn={turn} />
                        </React.Fragment>
                    ))}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 1.75 }}>
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    placeholder={intl.formatMessage({ id: 'groupScores.chat_placeholder', defaultMessage: 'Ask a follow-up question about this chart…' })}
                    inputProps={{ 'data-testid': 'group-chat-input', 'aria-label': intl.formatMessage({ id: 'groupScores.chat_input_label', defaultMessage: 'Question' }) }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
                />
                <IconButton
                    data-testid="group-chat-send"
                    color="primary"
                    onClick={send}
                    disabled={!draft.trim() || busy}
                    aria-label={intl.formatMessage({ id: 'groupScores.chat_send', defaultMessage: 'Send' })}
                    sx={{ bgcolor: '#fff', border: '1px solid #e6ebf2', '&:hover': { bgcolor: '#eef3fb' } }}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}
